import { cn } from '@/lib/utils';

export interface PopupCtaProps {
  label: string;
  onClick: () => void;
  /** Max button width in px (Figma: 320 big confirmations, 270/234 action variants). */
  width?: number;
  disabled?: boolean;
  testId?: string;
}

/**
 * The pop-up's primary CTA button, styled to the ARC-111 pop-up design system:
 * height 48, fully-rounded, brand purple (`--purple` #721cb7, lifts in dark),
 * white Outfit SemiBold 20px. Distinct from the app's default magenta-gradient
 * `Button` — the pop-ups are their own coherent Figma set — but built from the
 * same semantic tokens so it themes correctly. Full-width up to its Figma cap so
 * it shrinks gracefully on a narrow sheet.
 */
export function PopupCta({ label, onClick, width = 320, disabled, testId }: PopupCtaProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{ maxWidth: width }}
      className={cn(
        'flex h-12 w-full items-center justify-center rounded-full px-6',
        // Figma applies a `capitalize` transform to every CTA label regardless
        // of the underlying string casing — match it here once for all views.
        'bg-purple text-white font-sans text-xl font-semibold capitalize tracking-[-0.4px]',
        'transition-opacity hover:opacity-90 active:opacity-80',
        'disabled:pointer-events-none disabled:opacity-50',
      )}
    >
      {label}
    </button>
  );
}

export interface PopupLinkCtaProps {
  label: string;
  onClick: () => void;
  testId?: string;
}

/**
 * Secondary text-link CTA under the primary button (e.g. "Contact Support" on
 * the withdrawal-failed pop-up): underlined brand-purple Outfit. Forced LTR —
 * the Figma node was authored `dir="rtl"`.
 */
export function PopupLinkCta({ label, onClick, testId }: PopupLinkCtaProps) {
  return (
    <button
      type="button"
      dir="ltr"
      onClick={onClick}
      data-testid={testId}
      className="font-sans text-xl capitalize text-purple underline underline-offset-2 transition-opacity hover:opacity-80"
    >
      {label}
    </button>
  );
}
